// frontend/src/operator/OperatorLogin.js
// 運営者専用ログイン画面（秘密URL配下）
// メール+パスワード+Turnstile 認証 → is_superadmin チェック → MFA enroll/challenge → ダッシュボード

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import TurnstileWidget from '../components/TurnstileWidget';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const OPERATOR_PATH = process.env.REACT_APP_OPERATOR_PATH || 'ops-7k3m9x2p4r8q';

// State machine の各 step
const STEP = {
  CREDENTIALS: 'credentials',   // email + password + turnstile
  MFA_ENROLL: 'mfa_enroll',     // 初回 MFA 登録
  MFA_CHALLENGE: 'mfa_challenge', // 既存 MFA 検証
};

export default function OperatorLogin() {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEP.CREDENTIALS);

  // Credentials step
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');

  // MFA state
  const [factorId, setFactorId] = useState('');
  const [enrollData, setEnrollData] = useState(null); // { qr_code, secret }
  const [challengeId, setChallengeId] = useState('');
  const [mfaCode, setMfaCode] = useState('');

  // UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 既にログイン済みで AAL2 なら即ダッシュボードへ
  useEffect(() => {
    const check = async () => {
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aal?.currentLevel === 'aal2') {
        const { data: isSuper } = await supabase.rpc('is_superadmin');
        if (isSuper) {
          navigate(`/${OPERATOR_PATH}`, { replace: true });
        }
      }
    };
    check();
  }, [navigate]);

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!captchaToken) {
      setError('セキュリティチェックを完了してください');
      return;
    }

    setLoading(true);
    try {
      // 1. パスワード認証
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: { captchaToken },
      });

      if (signInError) throw new Error('メールアドレスまたはパスワードが正しくありません');

      // 2. is_superadmin チェック
      const { data: isSuper, error: rpcError } = await supabase.rpc('is_superadmin');
      if (rpcError || !isSuper) {
        await supabase.auth.signOut();
        throw new Error('運営権限がありません');
      }

      // 3. MFA 状態確認
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

      if (aal.currentLevel === 'aal2') {
        // 既に MFA 済み
        navigate(`/${OPERATOR_PATH}`, { replace: true });
        return;
      }

      // factor 一覧から検証済み TOTP を探す
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const verifiedTotp = factors?.totp?.find((f) => f.status === 'verified');

      if (verifiedTotp) {
        // factor 有り → challenge
        const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({
          factorId: verifiedTotp.id,
        });
        if (chErr) throw chErr;
        setFactorId(verifiedTotp.id);
        setChallengeId(ch.id);
        setStep(STEP.MFA_CHALLENGE);
      } else {
        // factor 無し → 新規 enroll
        const { data: enroll, error: enrollErr } = await supabase.auth.mfa.enroll({
          factorType: 'totp',
          friendlyName: 'ScrubEdge Operator',
        });
        if (enrollErr) throw enrollErr;
        setFactorId(enroll.id);
        setEnrollData(enroll.totp);
        setStep(STEP.MFA_ENROLL);
      }
    } catch (err) {
      setError(err.message || '認証に失敗しました');
      setCaptchaToken(''); // re-challenge
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!/^\d{6}$/.test(mfaCode)) {
      setError('6 桁の数字を入力してください');
      return;
    }

    setLoading(true);
    try {
      let cId = challengeId;

      // ENROLL の場合は challenge をここで実施
      if (step === STEP.MFA_ENROLL) {
        const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({ factorId });
        if (chErr) throw chErr;
        cId = ch.id;
      }

      const { error: verifyErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: cId,
        code: mfaCode,
      });

      if (verifyErr) throw new Error('コードが正しくありません');

      // 成功 → ダッシュボードへ
      navigate(`/${OPERATOR_PATH}`, { replace: true });
    } catch (err) {
      setError(err.message || 'MFA 検証に失敗しました');
      setMfaCode('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.title}>ScrubEdge 運営パネル</h1>
        <p style={styles.subtitle}>
          {step === STEP.CREDENTIALS && 'メールアドレスとパスワードでログイン'}
          {step === STEP.MFA_ENROLL && '認証アプリの初回設定'}
          {step === STEP.MFA_CHALLENGE && '認証アプリのコードを入力'}
        </p>

        {error && <div style={styles.error}>{error}</div>}

        {step === STEP.CREDENTIALS && (
          <form onSubmit={handleCredentialsSubmit}>
            <label style={styles.label}>メールアドレス</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              style={styles.input}
            />

            <label style={styles.label}>パスワード</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={styles.input}
            />

            <div style={styles.turnstile}>
              <TurnstileWidget
                siteKey={TURNSTILE_SITE_KEY}
                onVerify={(token) => setCaptchaToken(token)}
                onExpire={() => setCaptchaToken('')}
                onError={() => setCaptchaToken('')}
              />
            </div>

            <button
              type="submit"
              disabled={loading || !captchaToken}
              style={{ ...styles.button, ...(loading || !captchaToken ? styles.buttonDisabled : {}) }}
            >
              {loading ? '認証中...' : 'ログイン'}
            </button>
          </form>
        )}

        {step === STEP.MFA_ENROLL && enrollData && (
          <div>
            <p style={styles.helper}>
              下記 QR コードを Google Authenticator や 1Password などの認証アプリでスキャンしてください。
            </p>
            <div style={styles.qrContainer}>
              <img
                src={enrollData.qr_code}
                alt="MFA QR コード"
                style={{ width: 200, height: 200, display: 'block' }}
              />
            </div>
            <details style={styles.details}>
              <summary>QR が読めない場合（手動入力用シークレット）</summary>
              <code style={styles.secret}>{enrollData.secret}</code>
            </details>
            <form onSubmit={handleMfaSubmit}>
              <label style={styles.label}>アプリに表示された 6 桁コード</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                required
                autoFocus
                style={{ ...styles.input, fontSize: '1.5rem', letterSpacing: '0.5em', textAlign: 'center' }}
              />
              <button
                type="submit"
                disabled={loading}
                style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
              >
                {loading ? '確認中...' : '設定を完了'}
              </button>
            </form>
          </div>
        )}

        {step === STEP.MFA_CHALLENGE && (
          <form onSubmit={handleMfaSubmit}>
            <p style={styles.helper}>認証アプリを開いて、表示されている 6 桁コードを入力してください。</p>
            <label style={styles.label}>認証コード</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
              required
              autoFocus
              style={{ ...styles.input, fontSize: '1.5rem', letterSpacing: '0.5em', textAlign: 'center' }}
            />
            <button
              type="submit"
              disabled={loading}
              style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
            >
              {loading ? '確認中...' : '認証'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#0f172a',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '2rem 1rem',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  card: {
    background: '#fff',
    borderRadius: '12px',
    padding: '2.5rem 2rem',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#0f172a',
    margin: '0 0 0.25rem 0',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '0.9rem',
    color: '#64748b',
    margin: '0 0 1.5rem 0',
    textAlign: 'center',
  },
  label: {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#334155',
    marginBottom: '0.4rem',
    marginTop: '1rem',
  },
  input: {
    width: '100%',
    padding: '0.75rem 1rem',
    fontSize: '1rem',
    border: '1.5px solid #cbd5e1',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  turnstile: {
    display: 'flex',
    justifyContent: 'center',
    margin: '1.5rem 0 1rem 0',
    minHeight: '65px',
  },
  button: {
    width: '100%',
    padding: '0.85rem',
    background: '#0f172a',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '1rem',
  },
  buttonDisabled: {
    background: '#94a3b8',
    cursor: 'not-allowed',
  },
  error: {
    background: '#fef2f2',
    border: '1px solid #fca5a5',
    color: '#b91c1c',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    fontSize: '0.9rem',
    marginBottom: '1rem',
  },
  helper: {
    fontSize: '0.9rem',
    color: '#475569',
    lineHeight: 1.6,
    marginBottom: '1rem',
  },
  qrContainer: {
    display: 'flex',
    justifyContent: 'center',
    margin: '1rem 0',
    background: '#f8fafc',
    padding: '1rem',
    borderRadius: '8px',
  },
  details: {
    fontSize: '0.85rem',
    color: '#64748b',
    marginBottom: '1rem',
  },
  secret: {
    display: 'block',
    background: '#f1f5f9',
    padding: '0.5rem',
    borderRadius: '4px',
    marginTop: '0.5rem',
    fontSize: '0.85rem',
    wordBreak: 'break-all',
  },
};
